import React, { useState, useEffect, useCallback } from 'react';
import { FaCheck, FaUndo, FaSearch, FaArrowLeft, FaClipboardCheck, FaClock, FaTimes, FaBan } from 'react-icons/fa';
import styles from './styles/StockManager.module.css';
import vStyles from './styles/VerificacionRemitos.module.css';
import { useAuth } from '../../context/AuthContext';
import Pagination, { EMPTY_PAGINATION, PaginationMeta, readPaginated } from './Pagination';

interface RemitoItem {
    id: number;
    id_producto: number;
    nombre_producto: string;
    cantidad_enviada: number;
    cantidad_recibida: number | null;
    modificado_por: string | null;
    modificado_en: string | null;
}

interface Remito {
    id: number;
    transfer_id: string;
    id_local_origen: number;
    id_local_destino: number;
    nombre_origen: string;
    nombre_destino: string;
    usuario: string;
    fecha: string;
    estado: 'pendiente' | 'verificado' | 'con_diferencias' | 'rechazado';
    verificado_por: string | null;
    verificado_en: string | null;
    rechazado_por: string | null;
    rechazado_en: string | null;
    items?: RemitoItem[];
}

type TabView = 'pendientes' | 'aprobados' | 'rechazados';

const ESTADO_CONFIG = {
    pendiente:       { label: 'Pendiente',        bg: 'rgba(230,81,0,0.18)',   color: '#ffb74d', border: 'rgba(230,81,0,0.4)' },
    verificado:      { label: 'Verificado ✓',     bg: 'rgba(46,125,50,0.2)',   color: '#81c784', border: 'rgba(46,125,50,0.5)' },
    con_diferencias: { label: '⚠ Diferencias',    bg: 'rgba(243,156,18,0.18)', color: '#f39c12', border: 'rgba(243,156,18,0.5)' },
    rechazado:       { label: '✕ Rechazado',      bg: 'rgba(198,40,40,0.18)',  color: '#e57373', border: 'rgba(198,40,40,0.5)' },
};

const EstadoBadge: React.FC<{ estado: Remito['estado'] }> = ({ estado }) => {
    const cfg = ESTADO_CONFIG[estado];
    return (
        <span style={{
            padding: '4px 12px', borderRadius: '99px', fontSize: '0.76rem',
            fontWeight: 700, background: cfg.bg, color: cfg.color,
            border: `1px solid ${cfg.border}`, whiteSpace: 'nowrap', letterSpacing: '0.03em',
        }}>
            {cfg.label}
        </span>
    );
};

const VerificacionRemitos: React.FC = () => {
    const { user } = useAuth();

    const [tab, setTab] = useState<TabView>('pendientes');
    const [remitos, setRemitos] = useState<Remito[]>([]);
    const [meta, setMeta] = useState<PaginationMeta>(EMPTY_PAGINATION);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(50);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    const [detalle, setDetalle] = useState<Remito | null>(null);
    const [editedQty, setEditedQty] = useState<Record<number, number>>({});
    const [actionLoading, setActionLoading] = useState(false);
    const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    const showMsg = (type: 'ok' | 'err', text: string) => {
        setMsg({ type, text });
        setTimeout(() => setMsg(null), 5000);
    };

    const fetchRemitos = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            const estadoParam = tab === 'pendientes' ? 'pendiente' : tab === 'rechazados' ? 'rechazado' : 'aprobados';
            params.append('estado', estadoParam);
            params.append('page', String(page));
            params.append('per_page', String(perPage));
            // Todos los usuarios ven todos los remitos (sin filtrar por local de destino).
            const res = await fetch(`${API_URL}/admin/remitos?${params}`);
            if (res.ok) {
                const { items, pagination } = readPaginated<Remito>(await res.json());
                setRemitos(items);
                setMeta(pagination);
            }
        } catch {
            showMsg('err', 'Error al cargar remitos');
        } finally {
            setLoading(false);
        }
    }, [tab, API_URL, page, perPage]);

    useEffect(() => {
        fetchRemitos();
        setDetalle(null);
        setSearch('');
    }, [fetchRemitos]);

    // Cada tab lista un estado distinto: la pagina actual no aplica al nuevo.
    useEffect(() => {
        setPage(1);
    }, [tab, perPage]);

    const abrirDetalle = async (r: Remito) => {
        try {
            const res = await fetch(`${API_URL}/admin/remitos/${r.transfer_id}`);
            if (!res.ok) return;
            const data: Remito = await res.json();
            setDetalle(data);
            const init: Record<number, number> = {};
            data.items?.forEach(item => {
                init[item.id_producto] = item.cantidad_recibida ?? item.cantidad_enviada;
            });
            setEditedQty(init);
        } catch {
            showMsg('err', 'Error al cargar detalle');
        }
    };

    const handleVerificar = async () => {
        if (!detalle) return;
        setActionLoading(true);
        try {
            const items = detalle.items!.map(item => ({
                id_producto: item.id_producto,
                cantidad_recibida: editedQty[item.id_producto] ?? item.cantidad_enviada,
            }));
            const res = await fetch(`${API_URL}/admin/remitos/${detalle.transfer_id}/verificar`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ usuario: user || 'Admin', items }),
            });
            const data = await res.json();
            if (res.ok) {
                showMsg('ok', `Remito #${detalle.transfer_id} verificado correctamente`);
                setDetalle(null);
                fetchRemitos();
            } else {
                showMsg('err', data.error || 'Error al verificar');
            }
        } catch {
            showMsg('err', 'Error de conexión');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRechazar = async () => {
        if (!detalle) return;
        const ok = window.confirm(
            `⚠ ATENCIÓN: Vas a RECHAZAR el remito #${detalle.transfer_id}.\n\n` +
            `Este remito NO entrará a stock: se revertirá el movimiento completo ` +
            `(la mercadería vuelve a "${detalle.nombre_origen}" y se anula en "${detalle.nombre_destino}").\n\n` +
            `Quedará guardado en el historial como RECHAZADO a tu nombre.\n\n` +
            `¿Confirmás el rechazo?`
        );
        if (!ok) return;
        setActionLoading(true);
        try {
            const res = await fetch(`${API_URL}/admin/remitos/${detalle.transfer_id}/rechazar`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ usuario: user || 'Admin' }),
            });
            const data = await res.json();
            if (res.ok) {
                showMsg('ok', `Remito #${detalle.transfer_id} rechazado. El stock fue revertido.`);
                setDetalle(null);
                fetchRemitos();
            } else {
                showMsg('err', data.error || 'Error al rechazar');
            }
        } catch {
            showMsg('err', 'Error de conexión');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRevertir = async (transfer_id: string) => {
        setActionLoading(true);
        try {
            const res = await fetch(`${API_URL}/admin/remitos/${transfer_id}/revertir`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ usuario: user || 'Admin' }),
            });
            const data = await res.json();
            if (res.ok) {
                showMsg('ok', `Remito #${transfer_id} revertido a pendiente`);
                setDetalle(null);
                fetchRemitos();
            } else {
                showMsg('err', data.error || 'Error al revertir');
            }
        } catch {
            showMsg('err', 'Error de conexión');
        } finally {
            setActionLoading(false);
        }
    };

    const filteredRemitos = remitos.filter(r =>
        search === '' ||
        r.transfer_id.toLowerCase().includes(search.toLowerCase()) ||
        r.nombre_origen.toLowerCase().includes(search.toLowerCase()) ||
        r.nombre_destino.toLowerCase().includes(search.toLowerCase()) ||
        r.usuario.toLowerCase().includes(search.toLowerCase())
    );

    // ── Vista detalle ──────────────────────────────────────────────
    if (detalle) {
        const esPendiente = detalle.estado === 'pendiente';
        const esRechazado = detalle.estado === 'rechazado';
        const esAprobado = detalle.estado === 'verificado' || detalle.estado === 'con_diferencias';
        return (
            <div className={styles['stock-container']}>
                <button onClick={() => setDetalle(null)} className={vStyles.backBtn}>
                    <FaArrowLeft /> Volver a la lista
                </button>

                {msg && (
                    <div className={msg.type === 'ok' ? styles['success-msg'] : styles['error-msg']} style={{ marginBottom: '12px' }}>
                        {msg.text}
                    </div>
                )}

                {/* Cabecera del remito */}
                <div className={vStyles.detalleHeader}>
                    <div className={vStyles.detalleHeaderTop}>
                        <div>
                            <h3 className={vStyles.detalleTitle}>
                                Remito <span className={vStyles.transferId}>#{detalle.transfer_id}</span>
                            </h3>
                            <div className={vStyles.detalleMeta}>
                                <span><span className={vStyles.metaLabel}>Origen:</span> {detalle.nombre_origen}</span>
                                <span className={vStyles.metaArrow}>→</span>
                                <span><span className={vStyles.metaLabel}>Destino:</span> {detalle.nombre_destino}</span>
                                <span className={vStyles.metaSep}>·</span>
                                <span><span className={vStyles.metaLabel}>Enviado por:</span> {detalle.usuario}</span>
                                <span className={vStyles.metaSep}>·</span>
                                <span>{new Date(detalle.fecha).toLocaleString('es-AR')}</span>
                            </div>
                            {detalle.verificado_por && detalle.estado !== 'rechazado' && (
                                <div className={vStyles.verificadoPor}>
                                    <FaCheck style={{ marginRight: '5px', fontSize: '0.75rem' }} />
                                    Verificado por <strong>{detalle.verificado_por}</strong> · {new Date(detalle.verificado_en!).toLocaleString('es-AR')}
                                </div>
                            )}
                            {detalle.estado === 'rechazado' && detalle.rechazado_por && (
                                <div className={vStyles.verificadoPor} style={{ color: '#e57373' }}>
                                    <FaBan style={{ marginRight: '5px', fontSize: '0.75rem' }} />
                                    Rechazado por <strong>{detalle.rechazado_por}</strong>
                                    {detalle.rechazado_en && <> · {new Date(detalle.rechazado_en).toLocaleString('es-AR')}</>}
                                    <span style={{ marginLeft: '8px', opacity: 0.8 }}>— No ingresó a stock.</span>
                                </div>
                            )}
                        </div>
                        <EstadoBadge estado={detalle.estado} />
                    </div>
                </div>

                {/* Tabla de ítems */}
                <div className={vStyles.tableWrapper}>
                    <table className={vStyles.detalleTable}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left' }}>Producto</th>
                                <th>Enviado</th>
                                <th>{esPendiente ? 'Recibido (editable)' : 'Recibido'}</th>
                                <th>Diferencia</th>
                                {!esPendiente && <th>Modificado por</th>}
                                {!esPendiente && <th>Fecha modificación</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {detalle.items?.map(item => {
                                const recibido = esPendiente
                                    ? (editedQty[item.id_producto] ?? item.cantidad_enviada)
                                    : (item.cantidad_recibida ?? item.cantidad_enviada);
                                const diff = recibido - item.cantidad_enviada;
                                const hasDiff = diff !== 0;
                                return (
                                    <tr key={item.id} className={hasDiff ? vStyles.rowDiff : ''}>
                                        <td style={{ textAlign: 'left' }}>{item.nombre_producto}</td>
                                        <td>{item.cantidad_enviada}</td>
                                        <td>
                                            {esPendiente ? (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={editedQty[item.id_producto] ?? item.cantidad_enviada}
                                                    onChange={e => setEditedQty(prev => ({
                                                        ...prev,
                                                        [item.id_producto]: parseInt(e.target.value) || 0,
                                                    }))}
                                                    className={`${vStyles.qtyInput} ${hasDiff ? vStyles.qtyInputDiff : ''}`}
                                                />
                                            ) : (
                                                <span style={{ fontWeight: hasDiff ? 700 : 'normal' }}>{recibido}</span>
                                            )}
                                        </td>
                                        <td className={
                                            diff < 0 ? vStyles.diffNeg : diff > 0 ? vStyles.diffPos : vStyles.diffZero
                                        }>
                                            {diff === 0 ? '—' : (diff > 0 ? `+${diff}` : diff)}
                                        </td>
                                        {!esPendiente && (
                                            <td className={vStyles.metaCell}>{item.modificado_por || '—'}</td>
                                        )}
                                        {!esPendiente && (
                                            <td className={vStyles.metaCell}>
                                                {item.modificado_en ? new Date(item.modificado_en).toLocaleString('es-AR') : '—'}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Acciones */}
                <div className={vStyles.actionRow}>
                    {esPendiente && (
                        <>
                            <button
                                onClick={handleVerificar}
                                disabled={actionLoading}
                                className={vStyles.btnConfirmar}
                            >
                                <FaCheck /> {actionLoading ? 'Guardando...' : 'Confirmar Recepción'}
                            </button>
                            <button
                                onClick={handleRechazar}
                                disabled={actionLoading}
                                className={vStyles.btnRechazar}
                            >
                                <FaTimes /> {actionLoading ? 'Procesando...' : 'Rechazar Remito'}
                            </button>
                        </>
                    )}
                    {esAprobado && (
                        <button
                            onClick={() => handleRevertir(detalle.transfer_id)}
                            disabled={actionLoading}
                            className={vStyles.btnRevertir}
                        >
                            <FaUndo /> {actionLoading ? 'Revirtiendo...' : 'Revertir Aprobación'}
                        </button>
                    )}
                    {esRechazado && (
                        <span style={{ color: '#e57373', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <FaBan /> Remito rechazado — no ingresó a stock.
                        </span>
                    )}
                    <button onClick={() => setDetalle(null)} className={vStyles.btnCancelar}>
                        Cancelar
                    </button>
                </div>
            </div>
        );
    }

    // ── Vista lista ────────────────────────────────────────────────
    return (
        <div className={styles['stock-container']}>
            <h3 style={{ color: 'var(--secondary-color, #f39c12)', marginTop: 0, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaClipboardCheck /> Verificación de Remitos
            </h3>

            {/* Tabs */}
            <div className={vStyles.tabBar}>
                <button
                    onClick={() => setTab('pendientes')}
                    className={`${vStyles.tabBtn} ${tab === 'pendientes' ? vStyles.tabBtnActive : ''}`}
                >
                    <FaClock style={{ marginRight: '6px' }} /> Pendientes
                    {tab === 'pendientes' && remitos.length > 0 && (
                        <span className={vStyles.tabCount}>{remitos.length}</span>
                    )}
                </button>
                <button
                    onClick={() => setTab('aprobados')}
                    className={`${vStyles.tabBtn} ${tab === 'aprobados' ? vStyles.tabBtnActive : ''}`}
                >
                    <FaCheck style={{ marginRight: '6px' }} /> Aprobados
                </button>
                <button
                    onClick={() => setTab('rechazados')}
                    className={`${vStyles.tabBtn} ${tab === 'rechazados' ? vStyles.tabBtnActive : ''}`}
                >
                    <FaBan style={{ marginRight: '6px' }} /> Rechazados
                </button>
            </div>

            {msg && (
                <div className={msg.type === 'ok' ? styles['success-msg'] : styles['error-msg']} style={{ marginBottom: '12px' }}>
                    {msg.text}
                </div>
            )}

            {/* Barra de búsqueda */}
            <div className={vStyles.searchBar}>
                <div className={vStyles.searchWrapper}>
                    <FaSearch className={vStyles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Buscar por remito, local o usuario..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className={vStyles.searchInput}
                    />
                </div>
                <button onClick={fetchRemitos} className={vStyles.btnRefresh}>
                    🔄 Actualizar
                </button>
            </div>

            {/* Lista */}
            {loading ? (
                <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '40px 0' }}>Cargando...</p>
            ) : filteredRemitos.length === 0 ? (
                <div className={vStyles.emptyState}>
                    <FaClipboardCheck style={{ fontSize: '2.5rem', marginBottom: '12px', opacity: 0.3 }} />
                    <p>{tab === 'pendientes'
                        ? 'No hay remitos pendientes de verificación.'
                        : tab === 'rechazados'
                            ? 'No hay remitos rechazados.'
                            : 'No hay remitos aprobados aún.'}</p>
                </div>
            ) : (
                <div className={vStyles.remitoList}>
                    {filteredRemitos.map(r => (
                        <div key={r.transfer_id} className={vStyles.remitoCard} onClick={() => abrirDetalle(r)}>
                            <div className={vStyles.remitoCardLeft}>
                                <div className={vStyles.remitoCardTitle}>
                                    Remito <span className={vStyles.transferId}>#{r.transfer_id}</span>
                                </div>
                                <div className={vStyles.remitoCardRoute}>
                                    {r.nombre_origen} <span className={vStyles.routeArrow}>→</span> {r.nombre_destino}
                                </div>
                                <div className={vStyles.remitoCardMeta}>
                                    {new Date(r.fecha).toLocaleString('es-AR')} · Enviado por: <strong>{r.usuario}</strong>
                                </div>
                                {r.verificado_por && r.estado !== 'rechazado' && (
                                    <div className={vStyles.remitoCardVerif}>
                                        <FaCheck style={{ marginRight: '4px', fontSize: '0.7rem' }} />
                                        Verificado por <strong>{r.verificado_por}</strong> · {new Date(r.verificado_en!).toLocaleString('es-AR')}
                                    </div>
                                )}
                                {r.estado === 'rechazado' && r.rechazado_por && (
                                    <div className={vStyles.remitoCardVerif} style={{ color: '#e57373' }}>
                                        <FaBan style={{ marginRight: '4px', fontSize: '0.7rem' }} />
                                        Rechazado por <strong>{r.rechazado_por}</strong>
                                        {r.rechazado_en && <> · {new Date(r.rechazado_en).toLocaleString('es-AR')}</>}
                                    </div>
                                )}
                            </div>
                            <div className={vStyles.remitoCardRight}>
                                <EstadoBadge estado={r.estado} />
                                <span className={vStyles.verBtn}>Ver →</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <Pagination
                meta={meta}
                onPageChange={setPage}
                onPerPageChange={setPerPage}
                itemLabel="remitos"
            />
        </div>
    );
};

export default VerificacionRemitos;
